import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Loader2, Plus, Pencil, Trash2, MapPin, Eye, EyeOff, X, Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import AddressMapPicker from '../../components/AddressMapPicker';

const empty = { name: '', lat: 25.6866, lng: -100.3161, radius_meters: 5000, active: true, color: '#f97316' };

const CoverageSection = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modal, setModal] = useState(false);
  const [address, setAddress] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/admin/coverage/zones').then((r) => setZones(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ ...empty });
    setAddress({ lat: empty.lat, lng: empty.lng, address: '' });
    setModal(true);
  };
  const openEdit = (z) => {
    setEditing({ ...z });
    setAddress({ lat: z.lat, lng: z.lng, address: z.name });
    setModal(true);
  };

  const toggle = async (z) => {
    await api.patch(`/admin/coverage/zones/${z.id}`, { ...z, active: !z.active });
    toast({ title: !z.active ? 'Zona activada' : 'Zona desactivada' });
    load();
  };

  const save = async () => {
    if (!editing.name.trim()) { toast({ title: 'Nombre requerido' }); return; }
    if (!address?.lat) { toast({ title: 'Ubicación requerida', description: 'Busca la dirección o marca un punto en el mapa' }); return; }
    const payload = {
      name: editing.name.trim(),
      lat: Number(address.lat),
      lng: Number(address.lng),
      radius_meters: Number(editing.radius_meters) || 5000,
      active: !!editing.active,
      color: editing.color || '#f97316',
    };
    try {
      if (editing.id) {
        await api.patch(`/admin/coverage/zones/${editing.id}`, payload);
        toast({ title: 'Zona actualizada' });
      } else {
        await api.post('/admin/coverage/zones', payload);
        toast({ title: 'Zona creada' });
      }
      setModal(false);
      load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'No se guardó' }); }
  };

  const remove = async (z) => {
    if (!window.confirm(`¿Eliminar la zona "${z.name}"?`)) return;
    await api.delete(`/admin/coverage/zones/${z.id}`);
    toast({ title: 'Zona eliminada' });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white">Zonas de cobertura</h1>
          <p className="text-white/50 text-sm mt-1">Define círculos de entrega. Si no hay zonas configuradas, se cubre todo el país por defecto.</p>
        </div>
        <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-black font-bold shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Nueva zona
        </Button>
      </div>

      {/* Overview map */}
      <div className="mb-6 bg-neutral-950 border border-white/10 rounded-2xl p-3">
        <AddressMapPicker
          value={null}
          onChange={() => {}}
          zones={zones}
          height={280}
          interactive={false}
          showZones
        />
        <p className="text-white/50 text-xs mt-2 px-1">Vista general de las {zones.length} zonas configuradas.</p>
      </div>

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-orange-500" /> : (
        <div className="bg-neutral-950 border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-white/5 text-white/60">
              <tr>
                <th className="text-left px-3 py-2">Nombre</th>
                <th className="text-left px-3 py-2">Centro</th>
                <th className="text-right px-3 py-2">Radio</th>
                <th className="text-center px-3 py-2">Activa</th>
                <th className="text-right px-3 py-2 w-32">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-3 py-2 text-white font-semibold flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ background: z.color || '#f97316' }} />
                    {z.name}
                  </td>
                  <td className="px-3 py-2 text-white/60 text-xs font-mono">{z.lat.toFixed(4)}, {z.lng.toFixed(4)}</td>
                  <td className="px-3 py-2 text-right text-white/80">{(z.radius_meters / 1000).toFixed(1)} km</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => toggle(z)} className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${z.active ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}>
                      {z.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button onClick={() => openEdit(z)} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(z)} className="w-8 h-8 rounded-md bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 inline-flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {zones.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-white/50">
                  Sin zonas configuradas. Actualmente se acepta cobertura en todo México (fallback).
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="sm:max-w-2xl bg-neutral-950 border-white/10 text-white max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing?.id ? 'Editar zona' : 'Nueva zona de cobertura'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-white/60">Nombre de la zona</label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Ej. Monterrey Centro" className="bg-white/5 border-white/10 text-white" />
              </div>

              <div>
                <label className="text-xs text-white/60 mb-1 block">Ubicación (busca en el mapa o arrastra el marcador)</label>
                <AddressMapPicker
                  value={address}
                  onChange={setAddress}
                  zones={editing.id ? zones.filter((z) => z.id !== editing.id) : zones}
                  height={280}
                />
                {address?.lat && (
                  <p className="text-xs text-white/50 mt-2 font-mono">Centro: {address.lat.toFixed(4)}, {address.lng.toFixed(4)}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60">Radio (metros)</label>
                  <Input type="number" min={100} step={100} value={editing.radius_meters} onChange={(e) => setEditing({ ...editing, radius_meters: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                  <p className="text-[10px] text-white/40 mt-1">= {(editing.radius_meters / 1000).toFixed(1)} km</p>
                </div>
                <div>
                  <label className="text-xs text-white/60">Color en el mapa</label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={editing.color || '#f97316'} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="w-12 h-10 rounded-md bg-transparent border border-white/10 cursor-pointer" />
                    <Input value={editing.color || '#f97316'} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="bg-white/5 border-white/10 text-white flex-1" />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Zona activa
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setModal(false)} className="bg-transparent border-white/20 text-white hover:bg-white/10"><X className="w-4 h-4 mr-1" /> Cancelar</Button>
                <Button onClick={save} className="bg-orange-500 hover:bg-orange-600 text-black font-bold"><Save className="w-4 h-4 mr-1" /> Guardar zona</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoverageSection;
